import { db } from '../firebase';
import { collection, 
  addDoc, 
  updateDoc, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  where,
  orderBy,
  limit
 } from 'firebase/firestore';

export const useFirestore = () => {
  const addData = async (collectionName, data) => {
    try {

      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        isDeleted: false,
        createdAt: new Date(),
        updatedAt: new Date(),
      });

      return docRef.id;
    } catch (error) {
      console.error("Error adding document: ", error);
      throw error;  // Re-throw the error so it can be caught in the component
    }
  };

  const updateData = async (collectionName, docId, data) => {
    try {
      await updateDoc(doc(db, collectionName, docId), {
        ...data,
        updatedAt: new Date(),
      });
    } catch (error) {
      console.error("Error updating document: ", error);
    }
  };

  const getData = async (collectionName, docId) => {
    try {
      const docSnap = await getDoc(doc(db, collectionName, docId));
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        console.log("No such document!");
      }
    } catch (error) {
      console.error("Error getting document: ", error);
    }
  };

  const fetchData = async (collectionName, userId, singleDoc = false) => {
    try {
      if (collectionName === 'users') {
        // Special case for users collection
        const docRef = doc(db, 'users', userId);
        const docSnap = await getDoc(docRef);
        return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
      }

      let q = query(
        collection(db, collectionName), 
        where("userId", "==", userId),
        where("isDeleted", "==", false), 
        orderBy("createdAt", "desc")
      );

      if (singleDoc) {
        q = query(q, limit(1));
      }

      const querySnapshot = await getDocs(q);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      return singleDoc ? data[0] || null : data;
    } catch (error) {
      console.error(`Error fetching ${collectionName}:`, error);
      return singleDoc ? null : [];
    }
  };

  const fetchRecentUpdates = async (userId) => {
    const collections = ['goals', 'praises', 'evaluations', 'personalBrands', 'onlinePresence', 'professionalDevelopment', 'contacts'];
    const recentUpdates = [];

    for (const collectionName of collections) {
      try {
        const q = query(
          collection(db, collectionName),
          where('userId', '==', userId),
          orderBy('updatedAt', 'desc'),
          limit(3)  // Changed from 5 to 3
        );

        const snapshot = await getDocs(q);

        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.updatedAt) {
            recentUpdates.push({
              id: doc.id,
              type: collectionName,
              updatedAt: data.updatedAt.toDate(),
              details: getActivityDetails(collectionName, data)
            });
          }
        });
      } catch (error) {
        console.error(`Error fetching recent updates for ${collectionName}:`, error);
      }
    }

    return recentUpdates.sort((a, b) => b.updatedAt - a.updatedAt).slice(0, 3);  // Changed from 5 to 3
  };

  const getActivityDetails = (type, data) => {
    switch (type) {
      case 'goals':
        return `Updated goal: ${(data.text || 'Untitled').substring(0, 30)}...`;
      case 'praises':
        return `Received praise: ${(data.text || '').substring(0, 30)}...`;
      case 'evaluations':
        return 'Updated self-evaluation';
      case 'personalBrands':
        return 'Updated personal brand statement';
      case 'onlinePresence':
        return 'Updated online presence';
      case 'professionalDevelopment':
        return 'Updated professional development activities';
      case 'contacts':
        if (data.contacts && data.contacts.length > 0) {
          const lastUpdatedContact = data.contacts[data.contacts.length - 1];
          return `Updated contact: ${lastUpdatedContact.name || 'Unnamed'}`;
        }
        return 'Updated contacts';
      default:
        return 'Updated item';
    }
  };

  return { addData, updateData, getData, fetchData, fetchRecentUpdates };
};